import React, { useEffect, useMemo, useState } from 'react';
import { Paper, Box, Typography, Button, CircularProgress, Alert } from '@mui/material';
import { DetailHead, FilterBar, Field, SelectInput } from '../../components/common';
import DataTable, { Column } from '../../components/DataTable';
import { Avatar } from '../../components/NameCell';
import StatusChip from '../../components/StatusChip';
import { useTodaysQueue, useReportBusinesses } from '../../hooks/useReports';
import { statusLabel } from '../../utils/format';
import { colors } from '../../theme/theme';

const uniqSorted = (vals: (string | null)[]): string[] =>
  Array.from(new Set(vals.filter((v): v is string => !!v))).sort((a, b) => a.localeCompare(b));

const TodaysQueuePage: React.FC = () => {
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [businessId, setBusinessId] = useState('');
  const { data, loading, error, reload } = useTodaysQueue(businessId || undefined);
  const { data: businesses } = useReportBusinesses();
  const [active, setActive] = useState('all');

  const list = useMemo(() => businesses || [], [businesses]);

  // Location cascade: State → City → Business. Cities are constrained by the
  // chosen state; the business list is constrained by both.
  const stateOpts = useMemo(
    () => [{ value: '', label: 'All states' }, ...uniqSorted(list.map((b) => b.state_name)).map((s) => ({ value: s, label: s }))],
    [list],
  );
  const cityOpts = useMemo(
    () => [{ value: '', label: 'All cities' }, ...uniqSorted(list.filter((b) => !state || b.state_name === state).map((b) => b.city)).map((c) => ({ value: c, label: c }))],
    [list, state],
  );
  const filtered = useMemo(
    () => list.filter((b) => (!state || b.state_name === state) && (!city || b.city === city)),
    [list, state, city],
  );
  const businessOpts = useMemo(() => filtered.map((b) => ({ value: b.id, label: b.name })), [filtered]);

  // Keep the selected business valid for the active location filter. With no
  // state/city chosen, honour the backend-resolved busiest business; otherwise
  // fall back to the first business in the filtered set.
  useEffect(() => {
    if (!businesses) return;
    if (filtered.some((b) => b.id === businessId)) return;
    const resolved = data?.business?.id;
    const preferResolved = !state && !city && resolved && filtered.some((b) => b.id === resolved);
    setBusinessId(preferResolved ? resolved! : (filtered[0]?.id ?? ''));
  }, [businesses, filtered, businessId, state, city, data]);

  // Reset the status tab whenever the shown business changes (its counts differ).
  useEffect(() => { setActive('all'); }, [businessId]);

  const items = useMemo(() => data?.items ?? [], [data]);
  const counts = useMemo(() => data?.counts ?? {}, [data]);
  const statusKeys = useMemo(() => ['all', ...Object.keys(counts)], [counts]);
  const rows = useMemo(() => (active === 'all' ? items : items.filter((r) => r.status === active)), [items, active]);
  const countFor = (k: string) => (k === 'all' ? items.length : counts[k] ?? 0);

  const columns: Column<any>[] = [
    { key: 'token', label: 'Token #', render: (r) => <Typography component="span" sx={{ fontWeight: 600 }}>{r.token}</Typography> },
    { key: 'patient', label: 'Customer', render: (r) => <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}><Avatar name={r.patient} /><span>{r.patient}</span></Box> },
    { key: 'type', label: 'Type' },
    { key: 'phone', label: 'Phone', render: (r) => r.phone || '—' },
    { key: 'status', label: 'Status', render: (r) => <StatusChip status={r.status} label={statusLabel(r.status)} /> },
    { key: 'check_in', label: 'Check-in Time' },
  ];

  return (
    <>
      <DetailHead
        title="Today's Queue"
        sub={data ? `${data.business.name}${data.ref_date ? ` · ${data.ref_date}` : ''}` : 'Select a business to view its queue.'}
        actions={data ? <StatusChip status="info" label={data.ref_date ? `As of ${data.ref_date}` : 'No data'} /> : undefined}
      />

      <FilterBar>
        <Field label="State"><SelectInput value={state} onChange={(v) => { setState(v); setCity(''); }} options={stateOpts} minWidth={170} /></Field>
        <Field label="City"><SelectInput value={city} onChange={setCity} options={cityOpts} minWidth={170} /></Field>
        <Field label="Business"><SelectInput value={businessId} onChange={setBusinessId} options={businessOpts} minWidth={220} /></Field>
      </FilterBar>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: 300, alignItems: 'center' }}><CircularProgress /></Box>
      ) : error || !data ? (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={reload}>Retry</Button>}>{error || 'No data.'}</Alert>
      ) : (
        <Paper sx={{ p: 0 }}>
          <Box sx={{ p: 2, borderBottom: `1px solid ${colors.border}` }}>
            <Box sx={{ display: 'inline-flex', bgcolor: colors.gray100, borderRadius: '10px', p: '3px', gap: '2px', flexWrap: 'wrap' }}>
              {statusKeys.map((s) => (
                <Box key={s} onClick={() => setActive(s)}
                  sx={{
                    px: 1.75, py: 0.85, borderRadius: '8px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    bgcolor: active === s ? '#fff' : 'transparent',
                    color: active === s ? colors.primary : colors.textSecondary,
                    boxShadow: active === s ? '0 1px 2px rgba(16,24,40,.05)' : 'none',
                  }}>
                  {s === 'all' ? 'All' : statusLabel(s)} <Box component="span" sx={{ color: 'text.secondary' }}>{countFor(s)}</Box>
                </Box>
              ))}
            </Box>
          </Box>
          <DataTable columns={columns} rows={rows} pageSize={20} emptyText="No tokens for this day." />
        </Paper>
      )}
    </>
  );
};

export default TodaysQueuePage;
